import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Cropper from "react-easy-crop";
import { FiUpload } from "react-icons/fi";
import { FaArrowLeft, FaSpinner } from "react-icons/fa";
import { MdOutlineArrowForward } from "react-icons/md";
import api from "../../configs/api";
import toast from "react-hot-toast";
import uploadFileToCloudinary from "../../utils/uploadFileToCloudinary";
import { RefreshCw } from "lucide-react";

const AD_WIDTH = 356;
const AD_HEIGHT = 390;
const AD_ASPECT = AD_WIDTH / AD_HEIGHT;

const SuperAdminUpsertAdvertisements = () => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [ads, setAds] = useState(null);

  // Image states
  const [originalPreview, setOriginalPreview] = useState(null);
  const [originalCloudUrl, setOriginalCloudUrl] = useState(null);

  const [croppedPreview, setCroppedPreview] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [uploadingOriginal, setUploadingOriginal] = useState(false);
  const [uploadingCropped, setUploadingCropped] = useState(false);

  const fileRef = useRef(null);
  const livePreviewTimeout = useRef(null);

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const { data } = await api.get("/advertisements/admin");
        setAds(data);
        if (type === "top") {
          setOriginalCloudUrl(data.topAdOriginalUrl || data.topAdUrl || "");
          setCroppedPreview(data.topAdUrl || "");
        } else {
          setOriginalCloudUrl(
            data.bottomAdOriginalUrl || data.bottomAdUrl || ""
          );
          setCroppedPreview(data.bottomAdUrl || "");
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load advertisement");
      } finally {
        setLoading(false);
      }
    };

    fetchAds();
  }, [type]);

  // CROPPER HELPER
  async function getCroppedImg(imageSrc, pixelCrop) {
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = imageSrc;
    });

    const canvas = document.createElement("canvas");
    canvas.width = AD_WIDTH;
    canvas.height = AD_HEIGHT;

    const ctx = canvas.getContext("2d");

    const ratioX = img.naturalWidth / img.width;
    const ratioY = img.naturalHeight / img.height;

    const sx = pixelCrop.x * ratioX;
    const sy = pixelCrop.y * ratioY;
    const sw = pixelCrop.width * ratioX;
    const sh = pixelCrop.height * ratioY;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, AD_WIDTH, AD_HEIGHT);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.9)
    );

    const url = URL.createObjectURL(blob);
    return { blob, url };
  }

  // HANDLE FILE SELECT
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const blobUrl = URL.createObjectURL(file);
    setOriginalPreview(blobUrl);

    setUploadingOriginal(true);
    try {
      const { uploadPromise } = uploadFileToCloudinary(file);
      const { url } = await uploadPromise;

      setOriginalCloudUrl(url);
      toast.success("Original uploaded");
    } catch (err) {
      console.error(err);
      toast.error("Original upload failed");
    } finally {
      setUploadingOriginal(false);
      fileRef.current.value = "";
    }
  };

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  // HANDLE CROP + UPLOAD
  const handleCropAndUpload = async () => {
    if (!originalPreview && !originalCloudUrl) {
      return toast.error("Upload an image first");
    }

    const source = originalCloudUrl || originalPreview;

    setUploadingCropped(true);
    try {
      const { blob, url } = await getCroppedImg(source, croppedAreaPixels);
      setCroppedPreview(url);

      const { uploadPromise } = uploadFileToCloudinary(blob);
      const { url: uploadedUrl } = await uploadPromise;

      const payload =
        type === "top"
          ? { topAdUrl: uploadedUrl, topAdOriginalUrl: originalCloudUrl }
          : { bottomAdUrl: uploadedUrl, bottomAdOriginalUrl: originalCloudUrl };

      await api.put("/advertisements", payload);

      toast.success("Advertisement updated");
      navigate("/super-admin/advertisements");
    } catch (err) {
      console.error(err);
      toast.error("Crop or upload failed");
    } finally {
      setUploadingCropped(false);
    }
  };

  const generateLivePreview = async () => {
    if (!croppedAreaPixels) return;

    const source = originalCloudUrl || originalPreview;

    const { url } = await getCroppedImg(source, croppedAreaPixels);

    setCroppedPreview(url);
  };

  const triggerLivePreview = () => {
    if (!originalCloudUrl && !originalPreview) return;

    // clear last call
    if (livePreviewTimeout.current) clearTimeout(livePreviewTimeout.current);

    livePreviewTimeout.current = setTimeout(() => {
      generateLivePreview();
    }, 120);
  };

  const handleCropChange = (location) => {
    setCrop(location);
    triggerLivePreview();
  };

  const handleZoomChange = (value) => {
    setZoom(value);
    triggerLivePreview();
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center text-primary-navy">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-default">
      {/* HEADER */}
      <div className="flex items-center gap-3 px-6 py-4 bg-white shadow-sm">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded"
        >
          <FaArrowLeft className="text-primary-navy" />
        </button>
        <h1 className="text-xl font-semibold text-primary-navy">
          Update {type === "top" ? "Top" : "Bottom"} Advertisement
        </h1>
      </div>

      {/* MAIN LAYOUT */}
      <div className="flex flex-col lg:flex-row">
        {/* LEFT: Cropper + Instructions */}
        <div className="flex-1 p-6 space-y-4">
          {/* Upload */}
          <div className="flex items-center gap-4">
            <input
              type="file"
              accept="image/*"
              ref={fileRef}
              className="hidden"
              onChange={handleFileSelect}
            />
            <button
              onClick={() => fileRef.current.click()}
              className="inline-flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-gray-50"
            >
              <RefreshCw className="text-gray-500 size-4" />
              Change Image
            </button>

            {uploadingOriginal && (
              <span className="ml-3 text-sm text-gray-500 flex items-center gap-2">
                <FaSpinner className="animate-spin " /> Uploading...
              </span>
            )}
          </div>

          {/* Cropper */}
          {(originalPreview || originalCloudUrl) && (
            <div className="relative w-full aspect-[356/390] bg-black rounded-lg overflow-hidden max-h-[50vh]">
              <Cropper
                image={originalCloudUrl || originalPreview}
                crop={crop}
                zoom={zoom}
                aspect={AD_ASPECT}
                onCropChange={handleCropChange}
                onZoomChange={handleZoomChange}
                onCropComplete={onCropComplete}
                showGrid={true}
              />
            </div>
          )}

          {/* Controls */}
          {(originalPreview || originalCloudUrl) && (
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="text-sm text-gray-600">Zoom</label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.05}
                  className="w-full"
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                />
              </div>

              <button
                onClick={handleCropAndUpload}
                disabled={uploadingCropped}
                className="px-5 py-2 rounded-md bg-primary text-white hover:bg-[#5c49d6] flex items-center gap-2"
              >
                {uploadingCropped ? (
                  <FaSpinner className="animate-spin" />
                ) : (
                  <MdOutlineArrowForward />
                )}
                Save
              </button>
            </div>
          )}
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-800 mb-2">
              How to set the advertisement image
            </h3>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• Recommended ratio: 356 × 390 px</li>
              <li>• Upload a high-resolution image</li>
              <li>• Drag to position crop area</li>
              <li>• Use zoom slider to adjust framing</li>
              <li>• Click Save to finalize your cropped image</li>
            </ul>
          </div>
        </div>

        {/* RIGHT: Preview */}
        <div className="w-full lg:w-[380px] p-6">
          <h2 className="text-lg font-semibold text-primary-navy mb-4">
            Preview
          </h2>

          <div className="w-full aspect-[356/390] rounded-lg overflow-hidden border">
            <img
              src={
                croppedPreview ||
                originalCloudUrl ||
                originalPreview ||
                "https://via.placeholder.com/356x390?text=No+Image"
              }
              className="w-full h-full object-cover"
              alt=""
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminUpsertAdvertisements;
